import React, { useEffect, useState } from 'react'
import { useOutletContext } from 'react-router-dom'
import * as walletService from '../../services/walletService'
import { getAffiliateMembers } from '../../services/affiliateService'
import type { AffiliateOutletContext } from './ModernAffiliateLayout'
import type { AffiliateMemberItem } from '../../services/affiliateService'
import { affiliateMutedText, affiliateTopRankClass } from './affiliateUi'
import './affiliateLayout.css'

const TOP_TEN_LIMIT = 10

function formatJoinDate(joinedAt: string): string {
  try {
    const d = new Date(joinedAt)
    return Number.isNaN(d.getTime()) ? joinedAt : d.toLocaleString('th-TH', { dateStyle: 'medium', timeStyle: 'short' })
  } catch {
    return joinedAt
  }
}

function maskMember(raw: string): string {
  const s = String(raw || '').trim()
  if (!s) return 'สมาชิก'
  if (s.length < 4) return s
  if (/^\d+$/.test(s)) {
    if (s.length >= 10) return s.slice(0, 3) + 'xxxxxx' + s.slice(-1)
    return s.slice(0, 2) + 'xxxxx' + s.slice(-1)
  }
  if (s.length <= 6) return s.slice(0, 1) + 'xxx' + s.slice(-1)
  return s.slice(0, 2) + '****' + s.slice(-3)
}

const AffiliateToptenPage: React.FC = () => {
  useOutletContext<AffiliateOutletContext>()
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [members, setMembers] = useState<AffiliateMemberItem[]>([])

  useEffect(() => {
    let cancelled = false
    async function load() {
      setLoading(true)
      setError(null)
      try {
        const list = await getAffiliateMembers()
        if (!cancelled) setMembers(list.slice(0, TOP_TEN_LIMIT))
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : 'โหลดข้อมูลไม่สำเร็จ')
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    load()
    return () => {
      cancelled = true
    }
  }, [])

  if (loading) {
    return (
      <div className="text-center py-4">
        <div className="spinner-border text-primary" role="status" />
        <p className="mt-2 c_tertiary">กำลังโหลดข้อมูล...</p>
      </div>
    )
  }
  if (error) {
    return (
      <div className="text-center py-4">
        <p className="c_primary mb-0">{error}</p>
      </div>
    )
  }

  return (
    <>
      <div className="row mx__1 mb-3">
        <div className="col-12 px-1">
          <div className="affiliate-panel p-3 p-md-4 overflow-hidden">
            <div className="d-flex align-items-center gap-2">
              <div
                className="d-flex align-items-center justify-content-center flex-shrink-0"
                style={{
                  width: 40,
                  height: 40,
                  borderRadius: 12,
                  background: 'rgba(251, 191, 36, 0.18)',
                }}
              >
                <i className="fas fa-trophy" style={{ color: '#fbbf24' }} />
              </div>
              <div>
                <h5 className="c_tertiary mb-0">Top 10 สมาชิก</h5>
                <p className="mb-0 small" style={{ color: affiliateMutedText }}>
                  อันดับสมาชิกในเครือข่ายที่สมัครล่าสุด
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="row mx__1" style={{ rowGap: '0.75rem' }}>
        {members.length === 0 ? (
          <div className="col-12 px-1">
            <div className="affiliate-empty-state">
              <i className="fas fa-trophy mb-3" style={{ fontSize: '2rem', color: '#64748b' }} />
              <h4>ไม่มีรายการ</h4>
            </div>
          </div>
        ) : (
          members.map((member, index) => (
            <div key={`topten-${member.username}-${index}`} className="col-12 col-md-6 px-1">
              <div className="affiliate-member-card p-3 overflow-hidden h-100">
                <div className="d-flex align-items-start justify-content-between gap-2 mb-2">
                  <h5 className="font_md mb-0" style={{ color: '#f8fafc' }}>
                    {maskMember(member.username || member.displayName || 'สมาชิก')}
                  </h5>
                  <span className={affiliateTopRankClass(index)}>
                    {index < 3 ? (
                      <>
                        <i className="fas fa-medal me-1" />
                        #{index + 1}
                      </>
                    ) : (
                      <>#{index + 1}</>
                    )}
                  </span>
                </div>
                <div className="small mb-3" style={{ color: affiliateMutedText }}>
                  สมัครเมื่อวันที่ {formatJoinDate(member.joinedAt)}{' '}
                  <span className="affiliate-status-pill ms-1">
                    {member.status || 'สมาชิกปกติ'}
                  </span>
                </div>
                <div className="row g-2 mx-0">
                  <div className="col-6 ps-0">
                    <div className="affiliate-member-metric affiliate-member-metric--primary">
                      <h6>เดิมพันทั้งหมด</h6>
                      <h5>{walletService.formatCurrency(0)}</h5>
                    </div>
                  </div>
                  <div className="col-6 pe-0">
                    <div className="affiliate-member-metric affiliate-member-metric--secondary">
                      <h6>คอมมิชชั่น</h6>
                      <h5>{walletService.formatCurrency(0)}</h5>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </>
  )
}

export default AffiliateToptenPage
